import { useLanguage } from '@/contexts/language-context';
import type { JobDto, JobSummaryDto } from '@/lib/api/jobs';
import { fillTemplate, formatMoney, formatRelativeDays } from '@/lib/utils';

import { formatDate } from '../jobs/helpers';
import { LaborTile } from './labor-tile';
import { expectedPerVisit, laborPerVisit, profitPerVisit } from './helpers';

interface Props {
  summary: JobSummaryDto;
  job: JobDto | undefined;
}

interface TileProps {
  label: string;
  value: string;
  sub?: string;
  onClick?: () => void;
  testId: string;
  valueClassName?: string;
}

function Tile({ label, value, sub, onClick, testId, valueClassName }: TileProps) {
  const className =
    'flex flex-col gap-0.5 rounded-xl border bg-card px-4 py-3 text-left';
  const body = (
    <>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-lg font-semibold ${valueClassName ?? ''}`}>{value}</span>
      {sub && <span className="text-[11px] text-muted-foreground">{sub}</span>}
    </>
  );
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${className} hover:shadow-sm transition-shadow`}
      data-testid={testId}
    >
      {body}
    </button>
  ) : (
    <div className={className} data-testid={testId}>
      {body}
    </div>
  );
}

export function SeriesSummaryStrip({ summary, job }: Props) {
  const { t } = useLanguage();
  const { visits, labor } = summary;

  const perVisitLabor = laborPerVisit(labor);
  const perVisitExpected = expectedPerVisit(job);
  const perVisitProfit = profitPerVisit(perVisitExpected, perVisitLabor);

  const pipeline = fillTemplate(t.seriesPipelineSummary, {
    total: visits.total,
    completed: visits.completed,
    cancelled: visits.cancelled,
  });
  const nextLabel = summary.next_visit_date
    ? fillTemplate(t.seriesPipelineNext, {
        date: formatDate(summary.next_visit_date),
      })
    : t.seriesPipelineNoNext;

  const serviced = summary.last_completed_at
    ? fillTemplate(t.seriesLastServiced, {
        rel: formatRelativeDays(summary.last_completed_at),
      })
    : t.seriesNeverServiced;

  return (
    <section className="flex flex-col gap-3" data-testid="series-summary-strip">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="font-medium" data-testid="series-pipeline">
          {pipeline}
        </span>
        <span className="text-muted-foreground">· {nextLabel}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <LaborTile labor={labor} perVisit={perVisitLabor} />
        <Tile
          testId="series-tile-expected"
          label={t.seriesExpectedPerVisit}
          value={formatMoney(perVisitExpected)}
        />
        <Tile
          testId="series-tile-profit"
          label={t.seriesProfitPerVisit}
          value={formatMoney(perVisitProfit)}
          valueClassName={perVisitProfit < 0 ? 'text-destructive' : undefined}
        />
        <Tile
          testId="series-tile-visits"
          label={t.seriesVisitsCompleted}
          value={`${visits.completed} / ${visits.total}`}
        />
      </div>

      <p className="text-xs text-muted-foreground" data-testid="series-last-serviced">
        {serviced}
      </p>
    </section>
  );
}
